// src/components/BookingForm.jsx
import React, { useContext, useState, useRef, useEffect } from "react";
import "../styles/bookNow.css";
import { WebsiteContext } from "../context/AppContext";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronDown } from "react-icons/fa";

const CITIES = [
  "Coimbatore",
  "Chennai",
  "Madurai",
  "Bangalore",
  "Salem",
  "Trichy",
  "Karur",
  "Cochin",
  "Ooty",
];

const CITY_LOCALITIES = {
  Coimbatore: [
    "Gandhipuram",
    "Peelamedu",
    "RS Puram",
    "Saibaba Colony",
    "Singanallur",
    "Thudiyalur",
    "Ukkadam",
    "Vadavalli",
    "Saravanampatti",
    "Ramanathapuram",
    "Kuniamuthur",
    "Kovaipudur",
    "Kalapatti",
    "Hope College",
    "Railway Station",
    "Airport",
    "Other",
  ],
  Chennai: [
    "Adyar",
    "Anna Nagar",
    "Besant Nagar",
    "Egmore",
    "Guindy",
    "Mylapore",
    "Nungambakkam",
    "T Nagar",
    "Velachery",
    "Thiruvanmiyur",
    "Tambaram",
    "Porur",
    "Chromepet",
    "Pallavaram",
    "Chennai Central",
    "Airport",
    "Other",
  ],
  Madurai: [
    "Anna Nagar",
    "KK Nagar",
    "K Pudur",
    "Mattuthavani",
    "Simmakkal",
    "Tallakulam",
    "Thirunagar",
    "SS Colony",
    "Goripalayam",
    "Arapalayam",
    "Vasantha Nagar",
    "TVS Nagar",
    "Railway Station",
    "Airport",
    "Other",
  ],
  Bangalore: [
    "BTM Layout",
    "Electronic City",
    "HSR Layout",
    "Indiranagar",
    "Jayanagar",
    "Koramangala",
    "Marathahalli",
    "Whitefield",
    "Malleswaram",
    "Yelahanka",
    "Bellandur",
    "JP Nagar",
    "Majestic",
    "Airport",
    "Other",
  ],
  Salem: [
    "Ammapet",
    "Alagapuram",
    "Hasthampatti",
    "Fairlands",
    "Kondalampatti",
    "Meyyanur",
    "Suramangalam",
    "Gugai",
    "Shevapet",
    "Ayothiapattinam",
    "Kannankurichi",
    "Omalur",
    "New Bus Stand",
    "Railway Station",
    "Other",
  ],
  Trichy: [
    "Cantonment",
    "Chatram Bus Stand",
    "Central Bus Stand",
    "KK Nagar",
    "Srirangam",
    "Tennur",
    "Thillai Nagar",
    "Woraiyur",
    "Kattur",
    "Thiruverumbur",
    "Ponmalai",
    "Sengamalam",
    "Railway Station",
    "Airport",
    "Other",
  ],
  Karur: [
    "Gandhigramam",
    "Inam Karur",
    "Pasupathipalayam",
    "Thanthonimalai",
    "Vengamedu",
    "Sengunthapuram",
    "Rayanoor",
    "Vangapalayam",
    "Andankoil",
    "Velayuthampalayam",
    "Bus Stand",
    "Railway Station",
    "Other",
  ],
  Cochin: [
    "Edappally",
    "Fort Kochi",
    "Kacheripady",
    "Kaloor",
    "Marine Drive",
    "Palarivattom",
    "Vyttila",
    "Kakkanad",
    "Panampilly Nagar",
    "Ernakulam North",
    "Ernakulam South",
    "Willingdon Island",
    "Railway Station",
    "Airport",
    "Other",
  ],
  Ooty: [
    "Botanical Garden",
    "Charing Cross",
    "Commercial Road",
    "Coonoor Road",
    "Fern Hill",
    "Lake Road",
    "Mission Veng",
    "Rose Garden",
    "Stone House",
    "Lovedale",
    "Ketti",
    "Bus Stand",
    "Railway Station",
    "Other",
  ],
};

const PRICE_PER_KM = {
  Sedan: 14,
  SUV: 19,
  Innova: 20,
};

// Simple cache to avoid hitting Nominatim too often for the same queries
const geocodeCache = {};

// Custom Animated Dropdown Component
const AnimatedDropdown = ({
  name,
  value,
  onChange,
  placeholder,
  isMobileView,
  options,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    onChange({ target: { name, value: option } });
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        position: "relative",
        width: isMobileView ? "-webkit-fill-available" : "30vw",
        fontFamily: "Nexa-Regular",
      }}
    >
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={{
          width: "100%",
          height: 40,
          padding: "0px 10px",
          borderRadius: 2,
          border: "0.2px solid gray",
          backgroundColor: disabled ? "#f5f5f5" : "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          color: value ? "#000" : "#757575",
          boxSizing: "border-box",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <span>{value || placeholder}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <FaChevronDown size={14} color="#757575" />
        </motion.div>
      </div>

      <AnimatePresence>
        {isOpen && !disabled && (
          <motion.div
            initial={{ opacity: 0, y: -10, scaleY: 0.9 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -10, scaleY: 0.9 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            style={{
              position: "absolute",
              top: "calc(100% + 5px)",
              left: 0,
              right: 0,
              backgroundColor: "#fff",
              border: "1px solid #ccc",
              borderRadius: 4,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              zIndex: 10,
              maxHeight: 200,
              overflowY: "auto",
              transformOrigin: "top center",
            }}
          >
            {options?.map((option, index) => (
              <motion.div
                key={option}
                whileHover={{ backgroundColor: "#f0f0f0" }}
                onClick={() => handleSelect(option)}
                style={{
                  padding: "10px 15px",
                  cursor: "pointer",
                  borderBottom:
                    index === options.length - 1 ? "none" : "1px solid #eee",
                  color: "#333",
                }}
              >
                {option}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

function BookingForm() {
  const { isMobileView } = useContext(WebsiteContext);
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    cabType: "Sedan",
    tripType: "Round",
    pickupCity: "",
    dropCity: "",
    from: "",
    to: "",
    date: "",
    time: "",
  });

  const [distanceKm, setDistanceKm] = useState(null);
  const [approxPrice, setApproxPrice] = useState(null);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [calculationError, setCalculationError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      // Reset localities if their respective city changes
      if (name === "pickupCity") {
        newData.from = "";
      }
      if (name === "dropCity") {
        newData.to = "";
      }
      return newData;
    });
  };

  // Function to geocode a locality
  const getCoordinates = async (locality, city) => {
    if (locality === "Other") return null;

    const query = `${locality}, ${city}, India`;
    if (geocodeCache[query]) {
      return geocodeCache[query];
    }

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`,
      );
      const data = await response.json();
      if (data && data.length > 0) {
        const coords = {
          lat: parseFloat(data[0].lat),
          lon: parseFloat(data[0].lon),
        };
        geocodeCache[query] = coords;
        return coords;
      }
    } catch (error) {
      console.error("Geocoding error:", error);
    }
    return null;
  };

  // Effect to calculate distance and price whenever relevant fields change
  useEffect(() => {
    const calculateDistanceAndPrice = async () => {
      const { pickupCity, dropCity, from, to, cabType, tripType } = formData;

      // We need valid cities and localities (excluding "Other") to calculate distance automatically
      if (
        !pickupCity ||
        !dropCity ||
        !from ||
        !to ||
        from === "Other" ||
        to === "Other" ||
        (pickupCity === dropCity && from === to)
      ) {
        setDistanceKm(null);
        setApproxPrice(null);
        setCalculationError("");
        return;
      }

      setIsCalculatingPrice(true);
      setCalculationError("");

      try {
        const fromCoords = await getCoordinates(from, pickupCity);
        const toCoords = await getCoordinates(to, dropCity);

        if (!fromCoords || !toCoords) {
          setCalculationError(
            "Could not locate specific coordinates. Price will be calculated exactly after booking.",
          );
          setIsCalculatingPrice(false);
          return;
        }

        // Use OSRM for routing
        const osrmUrl = `https://router.project-osrm.org/route/v1/driving/${fromCoords.lon},${fromCoords.lat};${toCoords.lon},${toCoords.lat}?overview=false`;
        const response = await fetch(osrmUrl);
        const data = await response.json();

        if (data.code === "Ok" && data.routes && data.routes.length > 0) {
          // Exact distance from point A to point B in km
          let exactDistanceKm = data.routes[0].distance / 1000;

          if (tripType === "Round") {
            exactDistanceKm *= 2; // Exact driving distance is doubled for round trip
          }

          const rate = PRICE_PER_KM[cabType] || 14;
          const calculatedPrice = Math.ceil(exactDistanceKm * rate);

          setDistanceKm(exactDistanceKm.toFixed(1));
          setApproxPrice(calculatedPrice);
        } else {
          setCalculationError(
            "Routing service unavailable. Price will be confirmed later.",
          );
        }
      } catch (error) {
        setCalculationError(
          "Error calculating distance. Price will be confirmed later.",
        );
      } finally {
        setIsCalculatingPrice(false);
      }
    };

    // Add a slight debounce to prevent hammering the APIs
    const timer = setTimeout(() => {
      calculateDistanceAndPrice();
    }, 1000);

    return () => clearTimeout(timer);
  }, [
    formData.pickupCity,
    formData.dropCity,
    formData.from,
    formData.to,
    formData.cabType,
    formData.tripType,
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();

    const formatTime12Hour = (time24) => {
      if (!time24) return "";
      const [hours, minutes] = time24.split(":");
      const h = parseInt(hours, 10);
      const ampm = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      return `${h12}:${minutes} ${ampm}`;
    };

    let message =
      `*New Booking Request:* \n\n` +
      `Name: ${formData.name}\n` +
      `Mobile: ${formData.mobile}\n` +
      `Cab Type: ${formData.cabType}\n` +
      `Trip Type: ${formData.tripType}\n` +
      `Pickup City: ${formData.pickupCity}\n` +
      `Pickup Locality: ${formData.from}\n` +
      `Drop City: ${formData.dropCity}\n` +
      `Drop Locality: ${formData.to}\n` +
      `Date: ${formData.date}\n` +
      `Time: ${formatTime12Hour(formData.time)}`;

    if (approxPrice) {
      message += `\n\n*Estimated Distance:* ${distanceKm} km`;
      message += `\n*Approximate Price:* ₹${approxPrice}`;
    }

    const whatsappNumber = "919655590136";
    const url = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      message,
    )}`;
    setFormData({
      name: "",
      mobile: "",
      cabType: "Sedan",
      tripType: "Round",
      pickupCity: "",
      dropCity: "",
      from: "",
      to: "",
      date: "",
      time: "",
    });
    setDistanceKm(null);
    setApproxPrice(null);
    window.open(url, "_blank");
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const inputStyle = {
    width: isMobileView ? "-webkit-fill-available" : "30vw",
    height: 40,
    padding: "0px 10px",
    borderRadius: 2,
    border: "0.2px solid gray",
    outline: "none",
    fontFamily: "Nexa-Regular",
    boxSizing: "border-box", // Ensure padding doesn't affect total width
  };

  const pickupLocalities = formData.pickupCity ? CITY_LOCALITIES[formData.pickupCity] : [];
  const dropLocalities = formData.dropCity ? CITY_LOCALITIES[formData.dropCity] : [];

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="booking-title"
      >
        Enter Your Details to book your ride !
      </motion.div>
      <div
        style={{
          display: isMobileView ? "block" : "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: isMobileView ? "0px 20px" : 0,
        }}
      >
        <motion.form
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 20 }}
        >
          <motion.div variants={itemVariants}>
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              required
              onChange={handleChange}
              value={formData?.name}
              style={inputStyle}
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <input
              type="tel"
              name="mobile"
              placeholder="Mobile Number"
              required
              onChange={handleChange}
              value={formData?.mobile}
              style={inputStyle}
            />
          </motion.div>

          {/* Cab Type Dropdown */}
          <motion.div variants={itemVariants}>
            <select
              name="cabType"
              value={formData.cabType}
              onChange={handleChange}
              required
              style={inputStyle}
            >
              <option value="Sedan">Sedan</option>
              <option value="SUV">SUV</option>
              <option value="Innova">Innova</option>
            </select>
          </motion.div>

          {/* Trip Type Dropdown */}
          <motion.div variants={itemVariants}>
            <select
              name="tripType"
              value={formData.tripType}
              onChange={handleChange}
              required
              style={inputStyle}
            >
              <option value="Round">Round Trip</option>
              <option value="One">One Way Trip</option>
            </select>
          </motion.div>

          {/* Pickup City Selection Dropdown */}
          <motion.div
            variants={itemVariants}
            style={{ position: "relative", zIndex: 14 }}
          >
            <AnimatedDropdown
              name="pickupCity"
              value={formData.pickupCity}
              onChange={handleChange}
              placeholder="Select Pickup City"
              options={CITIES}
              isMobileView={isMobileView}
            />
          </motion.div>

          {/* Pickup Locality Dropdown */}
          <motion.div
            variants={itemVariants}
            style={{ position: "relative", zIndex: 13 }}
          >
            <AnimatedDropdown
              name="from"
              value={formData.from}
              onChange={handleChange}
              placeholder="Pickup Locality"
              options={pickupLocalities}
              disabled={!formData.pickupCity}
              isMobileView={isMobileView}
            />
          </motion.div>

          {/* Drop City Selection Dropdown */}
          <motion.div
            variants={itemVariants}
            style={{ position: "relative", zIndex: 12 }}
          >
            <AnimatedDropdown
              name="dropCity"
              value={formData.dropCity}
              onChange={handleChange}
              placeholder="Select Drop City"
              options={CITIES}
              isMobileView={isMobileView}
            />
          </motion.div>

          {/* Drop Locality Dropdown */}
          <motion.div
            variants={itemVariants}
            style={{ position: "relative", zIndex: 11 }}
          >
            <AnimatedDropdown
              name="to"
              value={formData.to}
              onChange={handleChange}
              placeholder="Drop Locality"
              options={dropLocalities}
              disabled={!formData.dropCity}
              isMobileView={isMobileView}
            />
          </motion.div>

          <motion.div variants={itemVariants}>
            <input
              type="date"
              name="date"
              required
              value={formData?.date}
              onChange={handleChange}
              style={inputStyle}
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <input
              type="time"
              name="time"
              required
              value={formData?.time}
              onChange={handleChange}
              style={inputStyle}
            />
          </motion.div>

          {/* Price Estimation Display */}
          <AnimatePresence>
            {isCalculatingPrice && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                style={{
                  textAlign: "center",
                  color: "#666",
                  fontSize: "0.9rem",
                  fontFamily: "Nexa-Regular",
                }}
              >
                Calculating estimated price...
              </motion.div>
            )}

            {!isCalculatingPrice && approxPrice && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                style={{
                  backgroundColor: "#e8f5e9",
                  padding: "10px",
                  borderRadius: "5px",
                  border: "1px solid #a5d6a7",
                  textAlign: "center",
                  color: "#2e7d32",
                  fontFamily: "Nexa-Bold",
                  width: isMobileView ? "-webkit-fill-available" : "30vw",
                  boxSizing: "border-box",
                }}
              >
                <h3>
                  Approximate Price: ₹{approxPrice.toLocaleString("en-IN")}
                </h3>
                <p
                  style={{
                    fontSize: "0.8rem",
                    margin: 0,
                    fontFamily: "Nexa-Regular",
                  }}
                >
                  Estimated Distance: {distanceKm} km
                </p>
              </motion.div>
            )}

            {!isCalculatingPrice && calculationError && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                style={{
                  textAlign: "center",
                  color: "#d32f2f",
                  fontSize: "0.8rem",
                  fontFamily: "Nexa-Regular",
                  width: isMobileView ? "-webkit-fill-available" : "30vw",
                }}
              >
                {calculationError}
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div variants={itemVariants}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="booking-submit-button"
            >
              Submit via WhatsApp
            </motion.button>
          </motion.div>
        </motion.form>
      </div>
    </>
  );
}

export default BookingForm;
